import { useEffect, useState } from 'react';
import { TICKS_PER_BAR, formatSeconds } from '../av/time';
import { EventBrowser } from '../components/sync/EventBrowser';
import { MappingEditor } from '../components/sync/MappingEditor';
import { MotionCuesPanel } from '../components/sync/MotionCuesPanel';
import { SyncPreview } from '../components/sync/SyncPreview';
import { SyncTimeline } from '../components/sync/SyncTimeline';
import { SyncTransport, TimingInspector } from '../components/sync/SyncTransport';
import { VideoExportPanel } from '../components/sync/VideoExportPanel';
import { useSceneEdit, type EditFn } from '../components/scene/useSceneEdit';
import { Alert, Button, Panel, inputClass } from '../components/ui';
import * as av from '../domain/avOps';
import type { AvSession, Project } from '../domain/schemas';
import { debugCounters } from '../lib/debug';
import { useObservable, useObservableSelector } from '../lib/useObservable';
import { useVisualHistory } from '../state/historyContext';
import { useProject } from '../state/projectContext';
import { useServices } from '../state/services';

/**
 * Sync workspace (Phase 4A): link music to a scene and an animation, map musical events to
 * reactive visual changes, and preview everything on one clock.
 *
 * What is what:
 *  - extracted events come from the arrangement (read-only); manual markers are yours;
 *  - reactions and motion cues are reactive overlays: they never change the scene, the
 *    animation's keyframes or the music;
 *  - keyframes (the animation) are permanent; "Bake into keyframes" is the only way cues
 *    become keyframes, as one undo step.
 */
export function SyncWorkspace() {
  const { project } = useProject();
  const { sync, syncSelection } = useServices();
  const sessionId = useObservableSelector(sync.state, (s) => s.sessionId);
  const history = useVisualHistory();
  const historyState = useObservable(history.state);
  const { edit, error, setError } = useSceneEdit();
  const [historyError, setHistoryError] = useState<string | null>(null);
  const session = project.avSessions.find((s) => s.id === sessionId) ?? null;

  useEffect(() => {
    debugCounters.syncDrift = () => sync.measureDrift();
    debugCounters.syncEvents = () => sync.events()?.events.map((e) => ({ id: e.id, category: e.category, tick: e.tick, seconds: e.seconds, velocity: e.velocity })) ?? null;
    return () => {
      debugCounters.syncDrift = () => null;
      debugCounters.syncEvents = () => null;
    };
  }, [sync]);
  // Leaving the workspace pauses session playback (it owns the shared audio engine).
  useEffect(() => () => sync.pause(), [sync]);

  const create = () => {
    let id = '';
    if (
      edit('New sync session', (p) => {
        const r = av.createSession(p);
        id = r.id;
        return r.project;
      })
    )
      syncSelection.selectSession(id);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="sync-workspace">
      <Panel
        title="Sync sessions"
        actions={
          <>
            <Button size="sm" onClick={() => setHistoryError(history.undo())} disabled={!historyState.canUndo} title={historyState.undoLabel ? `Undo ${historyState.undoLabel}` : 'Undo'} data-testid="sync-undo">
              ↶ Undo
            </Button>
            <Button size="sm" onClick={() => setHistoryError(history.redo())} disabled={!historyState.canRedo} title={historyState.redoLabel ? `Redo ${historyState.redoLabel}` : 'Redo'} data-testid="sync-redo">
              ↷ Redo
            </Button>
            <Button size="sm" variant="primary" onClick={create} disabled={project.avSessions.length >= 8} data-testid="sync-new">
              + New session
            </Button>
          </>
        }
      >
        {project.avSessions.length === 0 ? (
          <p className="text-sm text-zinc-400">
            A sync session plays this project’s music with a scene and its animation, and makes visuals react to musical events (kicks, snares, bars, your own markers). Create one, or type
            “synchronize this animation with the phonk track”.
          </p>
        ) : (
          <SessionSettings project={project} session={session} edit={edit} onSelect={(id) => syncSelection.selectSession(id)} />
        )}
        {(error || historyError) && (
          <div className="mt-3">
            <Alert title={error ?? historyError ?? ''} action={<Button size="sm" variant="ghost" onClick={() => (setError(null), setHistoryError(null))}>Dismiss</Button>} />
          </div>
        )}
      </Panel>
      {session && <SessionView project={project} session={session} edit={edit} />}
    </div>
  );
}

function SessionView({ project, session, edit }: { project: Project; session: AvSession; edit: EditFn }) {
  const { sync, syncSelection } = useServices();
  const sel = useObservable(syncSelection);
  // Only the duration: clock transitions (play, pause, seek) must not re-render this whole tree.
  const durationSec = useObservableSelector(sync.clock, (c) => c.timing.durationSec);
  const events = sync.events();
  if (!events) return null;
  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <section aria-labelledby="sync-preview-heading" className="flex flex-col gap-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
          <h2 id="sync-preview-heading" className="text-sm font-semibold tracking-wide text-zinc-200 uppercase">
            Preview · {session.name}
          </h2>
          {session.mappings.some((m) => m.reaction === 'flash') && (
            <p className="rounded bg-red-950 px-2 py-1 text-xs text-red-200" data-testid="sync-flash-warning">
              ⚠ This session flashes the screen. Rapid flashing can trigger seizures in people with photosensitive epilepsy: keep
              flashes subtle and infrequent, especially in videos you share.
            </p>
          )}
          <SyncPreview project={project} session={session} />
          <SyncTransport project={project} session={session} sync={sync} edit={edit} />
        </section>
        <Panel title="Timing">
          <TimingInspector project={project} session={session} events={events} sel={sel} sync={sync} />
          <ul className="mt-3 flex flex-col gap-1 text-xs text-zinc-400" aria-label="Legend">
            <li><span className="mr-1 inline-block size-2 rounded-sm bg-orange-500" aria-hidden="true" />Extracted events (from the arrangement)</li>
            <li><span className="mr-1 inline-block size-2 rotate-45 bg-pink-400" aria-hidden="true" />Manual markers (yours)</li>
            <li><span className="mr-1 inline-block size-2 rounded-sm bg-violet-400" aria-hidden="true" />Reactions and <span className="text-amber-400">motion cues</span>: overlays, never saved as keyframes</li>
            <li><span className="mr-1 inline-block size-2 rounded-sm bg-blue-300" aria-hidden="true" />Keyframes: the animation’s permanent keys</li>
          </ul>
        </Panel>
      </div>
      <Panel title="Timeline">
        <SyncTimeline project={project} session={session} events={events} durationSec={durationSec} sync={sync} selection={syncSelection} edit={edit} />
      </Panel>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title={`Reactions (${session.mappings.length})`}>
          <MappingEditor project={project} session={session} events={events} sel={sel} selection={syncSelection} edit={edit} />
        </Panel>
        <Panel title={`Events (${events.events.length})`}>
          <EventBrowser project={project} session={session} events={events} sync={sync} sel={sel} selection={syncSelection} edit={edit} />
        </Panel>
      </div>
      <Panel title={`Motion cues (${session.motionCues.length})`}>
        <MotionCuesPanel project={project} session={session} sel={sel} selection={syncSelection} edit={edit} />
      </Panel>
      <Panel title="Video export">
        <VideoExportPanel project={project} session={session} />
      </Panel>
    </>
  );
}

function SessionSettings({ project, session, edit, onSelect }: { project: Project; session: AvSession | null; edit: EditFn; onSelect: (id: string) => void }) {
  const { syncSelection } = useServices();
  if (!session) return null;
  const scenes = project.scenes;
  const animations = project.animations.filter((a) => a.sceneId === session.sceneId);
  const set = (label: string, patch: av.SessionPatch, key?: string) => edit(label, (p) => av.updateSession(p, session.id, patch), key ? `session:${session.id}:${key}` : undefined);
  const music = session.music;
  const bars = Math.round(av.musicTicks(project, session) / TICKS_PER_BAR);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Session
        <select className={inputClass} value={session.id} onChange={(e) => onSelect(e.target.value)} data-testid="sync-session-select">
          {project.avSessions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Name
        <input className={inputClass} value={session.name} maxLength={60} onChange={(e) => e.target.value.trim() && set('Rename session', { name: e.target.value }, 'name')} data-testid="sync-session-name" />
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Music
        <select
          className={inputClass}
          value={music.kind === 'song' ? 'song' : music.patternId}
          onChange={(e) => set('Change music', { music: e.target.value === 'song' ? { kind: 'song' } : { kind: 'pattern', patternId: e.target.value, bars: music.kind === 'pattern' ? music.bars : 4 } })}
          data-testid="sync-music"
        >
          <option value="song">Song (arrangement, {project.arrangement.sections.reduce((n, s) => n + s.bars, 0)} bars)</option>
          {project.patterns.map((p) => (
            <option key={p.id} value={p.id}>
              Pattern “{p.name}” (looped)
            </option>
          ))}
        </select>
      </label>
      {music.kind === 'pattern' && (
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Pattern bars
          <input className={inputClass} type="number" min={1} max={64} value={music.bars} onChange={(e) => set('Change bars', { music: { ...music, bars: Math.min(64, Math.max(1, Math.round(Number(e.target.value) || 1))) } }, 'bars')} />
        </label>
      )}
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Scene
        <select className={inputClass} value={session.sceneId} onChange={(e) => set('Change scene', { sceneId: e.target.value })} data-testid="sync-scene">
          {scenes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Animation
        <select className={inputClass} value={session.animationId ?? ''} onChange={(e) => set('Change animation', { animationId: e.target.value || null })} data-testid="sync-animation">
          <option value="">None (scene + reactions only)</option>
          {animations.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} · {a.fps} fps · {(a.durationFrames / a.fps).toFixed(1)} s
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Animation starts at bar
        <input
          className={inputClass}
          type="number"
          min={1}
          max={Math.max(1, bars)}
          value={Math.floor(session.animationStartTick / TICKS_PER_BAR) + 1}
          onChange={(e) => set('Change animation start', { animationStartTick: (Math.max(1, Math.round(Number(e.target.value) || 1)) - 1) * TICKS_PER_BAR }, 'start')}
          data-testid="sync-animation-start"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Length
        <select
          className={inputClass}
          value={session.durationMode}
          onChange={(e) => {
            const mode = e.target.value as AvSession['durationMode'];
            set('Change length', { durationMode: mode, customSeconds: mode === 'custom' ? (session.customSeconds ?? 30) : session.customSeconds });
          }}
        >
          <option value="music">Music ({formatSeconds(av.sessionDuration(project, { ...session, durationMode: 'music' }))})</option>
          <option value="animation" disabled={!session.animationId}>
            Animation
          </option>
          <option value="custom">Custom</option>
        </select>
      </label>
      {session.durationMode === 'custom' && (
        <label className="flex flex-col gap-1 text-xs text-zinc-400">
          Seconds
          <input className={inputClass} type="number" min={1} max={600} value={session.customSeconds ?? 30} onChange={(e) => set('Change length', { customSeconds: Math.min(600, Math.max(1, Number(e.target.value) || 1)) }, 'seconds')} />
        </label>
      )}
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        At the end
        <select className={inputClass} value={session.endBehavior} onChange={(e) => set('Change end behaviour', { endBehavior: e.target.value as AvSession['endBehavior'] })}>
          <option value="stop">Stop</option>
          <option value="loop">Loop the session</option>
        </select>
      </label>
      <div className="flex items-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (edit('Delete sync session', (p) => av.deleteSession(p, session.id))) syncSelection.selectSession(null);
          }}
          data-testid="sync-delete"
        >
          Delete session
        </Button>
      </div>
    </div>
  );
}
